import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod/v4";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { ArrowLeft } from "lucide-react";
import { useRedirectBack } from "./-utils";
import type { HTTPError } from "ky";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { api } from "@/lib/http";
import { PasswordInput } from "@/components/ui/passwordInput";
import { passwordRegex, passwordRegexErrorMessage } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { generateDefaultValue } from "@/lib/utils/zod";

const signupPageSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().optional(),
  otp: z.string().optional(),
});
export const Route = createFileRoute("/auth/forgot-password")({
  component: RouteComponent,
  validateSearch: signupPageSchema,
  staticData: {
    title: "Password Recovery",
  },
});

function RouteComponent() {
  const [email, setEmail] = useState<string | null>(null);

  return (
    <div>
      {email === null ? (
        <div>
          <RequestOtp onSuccess={setEmail} />
        </div>
      ) : (
        <div>
          <NewPassword email={email} />
        </div>
      )}
    </div>
  );
}

const RequestOtpSchema = z.object({
  email: z.string("Email is required").min(1, "Email is required"),
});
type RequestOtpSchema = z.infer<typeof RequestOtpSchema>;
function RequestOtp({ onSuccess }: { onSuccess: (email: string) => void }) {
  const searchParams = Route.useSearch();

  const form = useForm({
    resolver: standardSchemaResolver(RequestOtpSchema),
    defaultValues: generateDefaultValue(RequestOtpSchema),
  });
  const requestOtp = useMutation({
    async mutationFn({ email }: RequestOtpSchema) {
      await api.auth.post("users/forgot-password", { json: { email } });
    },
    onSuccess(_data, { email }) {
      onSuccess(email);
    },
    async onError(error: HTTPError) {
      const data = await error.response.json<{ detail?: string }>();
      form.setError("root.serverError", {
        message: data.detail || "An unexpected error occurred",
      });
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold">Forgot Password?</h1>
          <p className="text-sm text-gray-500">
            Enter your email so we can send you a password reset link.
          </p>
        </div>

        {/* Form */}
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (requestOtp.isPending) return;
              requestOtp.mutate(formData);
            })}
          >
            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black dark:text-white">
                    * Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      placeholder="Email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={requestOtp.isPending}
              className="w-full rounded-md bg-gray-700 py-3 font-medium text-white transition-colors hover:bg-gray-600 disabled:opacity-60"
            >
              {requestOtp.isPending ? "Sending..." : "Send Email"}
            </button>

            {/* Back to Login */}
            <Link
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "flex h-12 w-full items-center justify-center gap-2 text-sm",
              )}
              to="/auth/signin"
              search={{ redirect: searchParams.redirect }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Log in</span>
            </Link>

            {/* Server Error */}
            {form.formState.errors.root?.serverError && (
              <p className="text-sm text-primary">
                {form.formState.errors.root.serverError.message}
              </p>
            )}
          </form>
        </Form>
      </div>
    </main>
  );
}

const PasswordResetFormSchema = z.object({
  code: z.string().min(1, "OTP is required"),
  password: z
    .string()
    .min(1, "Password is required")
    .regex(passwordRegex, passwordRegexErrorMessage),
  confirm_password: z
    .string()
    .min(1, "Password is required")
    .regex(passwordRegex, passwordRegexErrorMessage),
});
type PasswordResetFormSchema = z.infer<typeof PasswordResetFormSchema>;
function NewPassword({ email }: { email: string }) {
  const redirect = useRedirectBack();

  const form = useForm({
    resolver: standardSchemaResolver(PasswordResetFormSchema),
    defaultValues: generateDefaultValue(PasswordResetFormSchema),
  });
  const requestOtp = useMutation({
    async mutationFn(formData: PasswordResetFormSchema) {
      await api.auth.post("users/reset-password-mail", {
        json: {
          ...formData,
          email,
        },
      });
    },
    onSuccess() {
      redirect();
    },
    async onError(error: HTTPError) {
      const data = await error.response.json<{ detail?: string }>();
      form.setError("root.serverError", {
        message: data.detail || "An unexpected error occurred",
      });
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center">
      <div className="w-full space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold">Reset Password</h1>
          <p className="text-gray-500">Please kindly set your new password</p>
        </div>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (requestOtp.isPending) return;
              requestOtp.mutate(formData);
            })}
          >
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* OTP Code</FormLabel>
                  <FormControl>
                    <Input placeholder="OTP Code" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* New Password</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="New Password" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* Confirm New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Confirm New Password"
                      {...field}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <button
              type="submit"
              disabled={requestOtp.isPending}
              className="w-full cursor-pointer rounded-md bg-gray-700 py-3 font-medium text-white transition-colors hover:bg-gray-600"
            >
              {requestOtp.isPending ? "Resetting..." : "Reset Password"}
            </button>
            {form.formState.errors.root?.serverError ? (
              <p className="text-sm text-primary">
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}
          </form>
        </Form>
      </div>
    </main>
  );
}
